import './Input.scss';
import * as React from 'react';
import { InputInterface } from './InputInterface';
import { RequiredInterface } from '../Required/RequiredInterface';
import { formatRequiredIndicator } from '../Required/Required';

export interface InputState<T = string> {
  value?: T;
  stringValue?: string;
  patternValue?: string;
  hasBlurred?: boolean;
}

export interface InputPropsInterface extends InputInterface, RequiredInterface {
  showCounter?: boolean;
  maxLength?: number;
  type?: 'text' | 'number';
}

export class Input extends React.Component<InputPropsInterface, InputState> {
  constructor(props: InputPropsInterface) {
    super(props);
    this.state = {
      value: props.value || '',
      stringValue: props.value || '',
      hasBlurred: true
    };
  }

  private input: HTMLInputElement;

  public componentWillReceiveProps(props: InputPropsInterface) {
    if (this.state.hasBlurred) {
      this.setState({
        value: this.handleFormatting(props.value),
        stringValue: this.handleFormatting(props.value),
        hasBlurred: true
      });
    }
  }

  public static defaultProps: InputPropsInterface = {
    value: '',
    className: ['Text'],
    automation: '',
    editable: true,
    borderless: false,
    autoFocus: false,
    type: 'text',
    disabled: false
  };

  private handleOnBlur(e: Event) {
    this.setState({
      ...this.state,
      hasBlurred: true
    });

    if (this.props.onBlur) {
      this.props.onBlur(this.state.value, this.state.stringValue);
      (e.target as HTMLInputElement).value = this.state.value;
    }
  }

  private handleFormatting(value: string): string {
    if (this.props.formatter) {
      return this.props.formatter.render(this.props.formatter.read(value));
    }
    return value;
  }

  private handlePattern(value: string): string {
    if (!this.props.pattern) {
      return value;
    }
    if (value.match(this.props.pattern)) {
      return value;
    } else {
      return this.state.patternValue;
    }
  }

  private handleOnChange(e: Event) {
    if (this.props.editable === false) {
      return;
    }

    const stringValue: string = this.handlePattern((e.target as HTMLInputElement).value);
    const value = this.handleFormatting(stringValue);

    this.setState({
      value,
      stringValue,
      patternValue: stringValue,
      hasBlurred: false
    });

    if (this.props.onChange) {
      this.props.onChange(value, stringValue);
    }
  }

  private setFocusOnInput(input: HTMLInputElement): void {
    const length = input.value.length;
    input.focus();
    input.setSelectionRange(length, length);
  }

  public componentDidMount() {
    if (this.props.autoFocus && this.input) {
      this.setFocusOnInput(this.input);
    }
  }
  public render(): JSX.Element {
    const classes = ['Controlled', 'Input'];
    if (this.props.borderless) {
      classes.push('Borderless');
    }

    let label = null;
    if (this.props.label) {
      const showcounter = this.props.maxLength && this.props.showCounter;
      const counterSpan = showcounter ? (
        <span className="counter">
          {` (${this.state.value.length}/${this.props.maxLength})`}
        </span>
      ) : null;
      const required = formatRequiredIndicator(this.props);
      label = (
        <span
          className="Label"
          data-automation={this.props.automation + '-label'}
        >
          {this.props.label}{required}
          {counterSpan}
        </span>
      );
    }

    const baseClasses = ['Controlled', 'Input'];
    if (this.props.labelPosition && this.props.labelPosition === 'top') {
      baseClasses.push('LabelOnTop');
    }
    if (this.props.align && this.props.align === 'left') {
      baseClasses.push('JustifyLeft');
    }
    if (this.props.invalid) {
      baseClasses.push('invalid');
    }

    let value = '';
    if (this.props.onChange) {
      value = this.props.value;
    } else {
      value = this.state.stringValue;
    }
    return (
      <div className={baseClasses.join(' ')}>
        {label}
        <input
          ref={(input) => {
            this.input = input;
          }}
          className={classes.join(' ') + ' ' + this.props.className.join(' ')}
          data-automation={this.props.automation}
          data-value={this.props.dataValue || this.props.value}
          type={this.props.type}
          value={value}
          disabled={this.props.disabled}
          onChange={this.handleOnChange.bind(this)}
          onBlur={this.handleOnBlur.bind(this)}
          onClick={this.props.onClick}
          size={this.props.size}
          maxLength={this.props.maxLength}
          placeholder={this.props.placeHolder}
        />
      </div>
    );
  }
}
